import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  cancelDowngrade,
  cancelSubscription,
  getSubscriptionData,
} from 'src/features/SubscriptionSlice'
import Loading from 'src/components/styled/Loading'
import { FOOD_SAFETY_SURFACE } from 'src/constants/foodSafety'
import { getTeamFeatureGroup } from 'src/constants/planFeatureCategories'
import { recipePlanFeatureLines } from 'src/constants/recipePlanAllowances'
import { planDisplayName } from 'src/constants/planDisplay'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'

const KNOWN_PLANS = ['Pro', 'Growth', 'Starter', 'Free']

const planIncludedLines = title => {
  const lowerTitle = String(title || '').toLowerCase()
  // Internal keys stay 'Free'; also match the customer-facing 'OMS Lite' title.
  const name = KNOWN_PLANS.find(
    plan =>
      lowerTitle.includes(plan.toLowerCase()) ||
      (plan === 'Free' && lowerTitle.includes('lite')),
  )
  if (!name) return []
  const recipes = recipePlanFeatureLines(name).map(line => line.text)
  const team = getTeamFeatureGroup(name)
    .features.slice(0, 2)
    .map(item => (typeof item === 'string' ? item : item.text))
  return [...recipes, ...team]
}

const statusLabel = status => {
  const raw = String(status || 'unknown')
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

const formatPeriod = unix => {
  if (!unix) return null
  const date = new Date(unix * 1000)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const SubscriptionDetails = ({ subscriptionData, subscriptionKey }) => {
  const [subscription, setSubscription] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(
    Boolean(subscriptionData?.subscriptionId),
  )
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue.venue?._id)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const cancelSubscriptionScheduled =
    useSelector(state => state.venue.venue?.cancelSubscriptionScheduled) ?? {}
  const isCancelled =
    cancelSubscriptionScheduled?.subscriptionId ===
    subscriptionData.subscriptionId

  const onCancelSubscription = async () => {
    setLoading(true)
    let response
    if (isCancelled) {
      response = await dispatch(
        cancelDowngrade(cancelSubscriptionScheduled?._id),
      )
    } else {
      response = await dispatch(
        cancelSubscription(subscriptionData?.subscriptionId, venueId),
      )
    }

    if (response?.success) {
      setLoading(false)
      if (isCancelled) {
        SuccessNotification({
          message: 'Downgrade',
          description: `✅ ${response?.message}`,
        })
      } else {
        SuccessNotification({
          message: 'Downgrade',
          description: `✅ Your downgrade to ${planDisplayName(
            'Free',
          )} has been scheduled. It will take effect on ${new Date(
            subscription.current_period_end * 1000,
          ).toLocaleDateString()}`,
        })
      }
    } else {
      setLoading(false)
      RejectedNotification({
        message: 'Error',
        description: response?.error || 'An Unknown Error Occured',
      })
    }

    setTimeout(() => {
      window.location.reload()
    }, 2000)
  }

  useEffect(() => {
    let cancelled = false

    const fetchSubscriptionData = async () => {
      setDetailsLoading(true)
      try {
        const data = await dispatch(
          getSubscriptionData(subscriptionData?.subscriptionId),
        )
        if (!cancelled) setSubscription(data || null)
      } catch (error) {
        if (!cancelled) setSubscription(null)
      } finally {
        if (!cancelled) setDetailsLoading(false)
      }
    }

    if (subscriptionData?.subscriptionId) {
      fetchSubscriptionData()
    } else {
      setDetailsLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [dispatch, subscriptionData?.subscriptionId])

  const giftedPlans = {
    starter: {
      title: 'Starter',
      price: 29,
    },
    growth: {
      title: 'Growth',
      price: 79,
    },
    pro: {
      title: 'Pro',
      price: 149,
    },
    addSuppliers: {
      title: 'Unlimited Suppliers',
      price: 10,
    },
    creditNotesAutomation: {
      title: 'Credit Notes Automation',
      price: 20,
    },
    invoiceStoarge: {
      title: 'Invoice Storage',
      price: 8,
    },
    omsplus: {
      title: 'OMS Plus',
      price: 49.99,
    },
    pricetracking: {
      title: 'Price Tracking',
      price: 20,
    },
    reconcilation: {
      title: 'Full Invoice Reconciliation',
      price: 35,
    },
  }

  const previousPlans = [
    'Unlimited Suppliers',
    'Credit Notes Automation',
    'Invoice Storage',
    'OMS Plus',
    'Price Tracking',
    'Full Invoice Reconciliation',
  ]
  const billedTitle = subscription?.plan?.nickname || 'N/A'
  const showPrice =
    subscription?.plan && !previousPlans.includes(subscription.plan.nickname)
  const price =
    showPrice && subscription.plan.amount != null
      ? `£${subscription.plan.amount / 100} a ${
          subscription.plan.interval || 'month'
        }`
      : null
  const periodStart = formatPeriod(subscription?.current_period_start)
  const periodEnd = formatPeriod(subscription?.current_period_end)
  const billingFacts = subscription
    ? [
        periodStart && { label: 'Period started', value: periodStart },
        periodEnd && {
          label: isCancelled ? `Moves to ${planDisplayName('Free')}` : 'Renews',
          value: periodEnd,
        },
        {
          label: 'Payment',
          value:
            subscription.collection_method === 'charge_automatically'
              ? 'Card, charged automatically'
              : 'Card, charged manually',
        },
      ].filter(Boolean)
    : []

  return (
    <Wrapper>
      {detailsLoading ? (
        <PlanBand>
          <Loading />
        </PlanBand>
      ) : subscription ? (
        <PlanSummary
          title={billedTitle}
          status={subscription.status}
          price={price}
          facts={billingFacts}
          action={
            <QuietButton
              type='button'
              onClick={() => setShowConfirmModal(true)}
            >
              {loading
                ? 'Scheduling Downgrade...'
                : isCancelled
                ? 'Cancel Downgrade'
                : `Downgrade to ${planDisplayName('Free')}`}
            </QuietButton>
          }
        />
      ) : subscriptionData?.isSubscribed ? (
        <PlanSummary
          title={giftedPlans[subscriptionKey]?.title || 'N/A'}
          status='active'
          note='Activated by OMS Admin'
        />
      ) : null}

      {showConfirmModal && (
        <ModalOverlay>
          <ModalCard>
            <h2>
              {isCancelled
                ? 'Confirm Cancel Downgrade'
                : `Confirm Downgrade to ${planDisplayName('Free')}`}
            </h2>
            {isCancelled ? (
              <>
                <p>You are about to cancel sshceduled downgrade</p>
              </>
            ) : (
              <>
                {' '}
                <p>
                  You are about to schedule a downgrade to{' '}
                  {planDisplayName('Free')}. You will continue to have full
                  access to your current features until your billing period ends
                  on{' '}
                  <strong>
                    {subscription?.current_period_end
                      ? new Date(
                          subscription.current_period_end * 1000,
                        ).toLocaleDateString()
                      : 'N/A'}
                  </strong>
                  .
                </p>
                <p>
                  After that, your account will automatically move to{' '}
                  {planDisplayName('Free')}, and you wont be charged again.
                </p>
                <p>
                  Need more time to decide? You can cancel this downgrade
                  anytime before{' '}
                  <strong>
                    {subscription?.current_period_end
                      ? new Date(
                          subscription.current_period_end * 1000,
                        ).toLocaleDateString()
                      : 'N/A'}
                  </strong>
                  .
                </p>
              </>
            )}

            <ModalActions>
              <ConfirmButton
                onClick={() => {
                  setShowConfirmModal(false)
                  onCancelSubscription()
                }}
              >
                {isCancelled
                  ? ` ✅ Confirm Cancel Downgrade`
                  : ` ✅ Confirm Downgrade`}
              </ConfirmButton>
              <CancelButton onClick={() => setShowConfirmModal(false)}>
                {isCancelled ? 'Cancel' : ` ❌ Keep Current Plan`}
              </CancelButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrapper>
  )
}

export const PlanSummary = ({ title, status, note, price, facts, action }) => {
  const lines = planIncludedLines(title)
  return (
    <PlanBand data-testid='subscription-plan-card'>
      <Eyebrow>Your plan</Eyebrow>
      <TitleRow>
        <PlanTitle>{title}</PlanTitle>
        <StatusPill data-status={String(status || '').toLowerCase()}>
          {statusLabel(status)}
        </StatusPill>
      </TitleRow>
      {price ? <Price>{price}</Price> : null}
      {note ? <Note>{note}</Note> : null}
      {lines.length > 0 ? (
        <Included aria-label='Included on this plan'>
          {lines.map(line => (
            <IncludedItem key={line}>{line}</IncludedItem>
          ))}
        </Included>
      ) : null}
      {facts?.length ? (
        <Facts>
          {facts.map(fact => (
            <Fact key={fact.label}>
              <FactLabel>{fact.label}</FactLabel>
              <FactValue>{fact.value}</FactValue>
            </Fact>
          ))}
        </Facts>
      ) : null}
      {action || null}
    </PlanBand>
  )
}

export default SubscriptionDetails

const Wrapper = styled.div`
  min-width: 0;
`

const PlanBand = styled.section`
  margin: 0;
  padding: 1rem 1.1rem 1.15rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: ${FOOD_SAFETY_SURFACE.HERO_BG};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  border-top: 3px solid
    ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
`

const Eyebrow = styled.p`
  margin: 0 0 0.2rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #555555;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

const PlanTitle = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const StatusPill = styled.span`
  flex: 0 0 auto;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  border: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  color: ${FOOD_SAFETY_SURFACE.STATUS_READY};
  font-size: 0.75rem;
  font-weight: 700;

  &[data-status='active'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }
`

const Price = styled.p`
  margin: 0.35rem 0 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const Note = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  color: #555555;
`

const Included = styled.ul`
  margin: 0.9rem 0 0;
  padding: 0.75rem 0 0;
  list-style: none;
  border-top: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
`

const IncludedItem = styled.li`
  position: relative;
  margin: 0;
  padding: 0.28rem 0 0.28rem 0.9rem;
  font-size: 0.9rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.62rem;
    width: 0.35rem;
    height: 0.35rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  }
`

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 0.65rem 1rem;
  margin: 0.9rem 0 0;
  padding: 0.75rem 0 0;
  border-top: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
`

const Fact = styled.div``

const FactLabel = styled.dt`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: #555555;
`

const FactValue = styled.dd`
  margin: 0.1rem 0 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
`

const QuietButton = styled.button`
  margin-top: 0.9rem;
  padding: 0.4rem 0.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  background: #fff;
  color: ${FOOD_SAFETY_SURFACE.STATUS_READY};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ModalCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  max-width: 500px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`

const ModalActions = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const ConfirmButton = styled.button`
  background-color: ${({ theme }) => theme.colours.lettuceGreen};
  color: white;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: #4e9b5f;
  }
`

const CancelButton = styled.button`
  background-color: #ccc;
  color: #333;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: #bbb;
  }
`
